import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchMetricCatalog,
  fetchMetricSeries,
  type MetricCatalogEntry,
  type MetricSeriesResponse,
} from '../../api/work';
import './pulse-panels.css';

/**
 * The catalogue, and one metric's series.
 *
 * The layer below `Observations`: this is where you go when an observation
 * cites a metric and you want to see how that metric has actually moved.
 *
 * **Loaded on open and on refresh, never on a timer.** The catalogue reads
 * `metric_runs` — 431,618 rows on wingd, ~300 ms — and the series reads
 * `metric_values` through `idx_metric_scope_ts`. Both are cheap enough to
 * ask for and too expensive to poll, so nothing here has an interval.
 *
 * **A stale metric is the finding this tab exists for.** A metric that did
 * not run in the latest pass is in the catalogue and computing nothing,
 * and no other view can show that: absence of rows reads identically to
 * absence of subjects. They sort first.
 *
 * **An aggregate series is labelled as one.** With no scope selected the
 * route returns a mean across every scope, and a mean over metrics that
 * are variously counts, shares and hours is a shape, not a quantity. The
 * chart says so rather than implying a number.
 */

const WINDOWS = [7, 14, 30, 90];

function relative(ms: number | null): string {
  if (ms === null) return 'never';
  const min = Math.floor((Date.now() - ms) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function dur(v: number | null): string {
  if (v === null) return '—';
  if (v < 1000) return `${Math.round(v)}ms`;
  return `${(v / 1000).toFixed(1)}s`;
}

function compact(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

/** One `<path>` per unbroken run, so a gap is drawn as a gap. */
function Chart({ series }: { series: MetricSeriesResponse }) {
  const { paths, lo, hi } = useMemo(() => {
    const pts = series.points;
    const vals = pts.map((p) => p.value).filter((v): v is number => v !== null);
    if (vals.length === 0) return { paths: [], lo: 0, hi: 0 };
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const span = max - min || 1;
    const w = 640;
    const h = 120;
    const x = (i: number) => (pts.length === 1 ? w / 2 : (i / (pts.length - 1)) * w);
    const y = (v: number) => h - ((v - min) / span) * (h - 8) - 4;
    const runs: string[] = [];
    let cur: string[] = [];
    pts.forEach((p, i) => {
      if (p.value === null) {
        if (cur.length) runs.push(cur.join(' '));
        cur = [];
        return;
      }
      cur.push(`${cur.length === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`);
    });
    if (cur.length) runs.push(cur.join(' '));
    return { paths: runs, lo: min, hi: max };
  }, [series]);

  if (paths.length === 0) {
    return <p className="pp__dim">No finite values in this window.</p>;
  }
  return (
    <div className="pp__chart">
      <div className="pp__chart-axis">
        <span>{hi === lo ? hi : hi}</span>
        <span>{lo}</span>
      </div>
      <svg viewBox="0 0 640 120" preserveAspectRatio="none" role="img"
           aria-label={`${series.name} from ${lo} to ${hi}`}>
        {paths.map((d, i) => (
          <path key={i} d={d} fill="none" strokeWidth="1.5" />
        ))}
      </svg>
    </div>
  );
}

export function MetricsPanel() {
  const [catalog, setCatalog] = useState<MetricCatalogEntry[] | null>(null);
  const [latestPass, setLatestPass] = useState<number | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [scope, setScope] = useState('');
  const [days, setDays] = useState(14);
  const [series, setSeries] = useState<MetricSeriesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seriesError, setSeriesError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(() => {
    setCatalog(null);
    fetchMetricCatalog()
      .then((r) => {
        // Problems first, then alphabetical. Three kinds of problem, in
        // the order they matter: a metric with runs and no definition was
        // retired and its rows are stale by 170 days here; one that never
        // ran is registered and doing nothing; one that is unimplemented
        // was declined on purpose and is the mildest of the three.
        const rank = (m: MetricCatalogEntry) =>
          (!m.registered ? 4 : 0) +
          (m.stale ? 2 : 0) +
          (m.never_ran && !m.unimplemented ? 1 : 0);
        const rows = [...r.metrics].sort(
          (a, b) => rank(b) - rank(a) || a.name.localeCompare(b.name),
        );
        setCatalog(rows);
        setLatestPass(r.latest_pass_ms);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    if (!selected) return;
    let live = true;
    setSeries(null);
    setSeriesError(null);
    fetchMetricSeries(selected, { days, scope: scope || undefined })
      .then((s) => live && setSeries(s))
      .catch((e: unknown) =>
        live && setSeriesError(e instanceof Error ? e.message : String(e)),
      );
    return () => {
      live = false;
    };
  }, [selected, days, scope]);

  const visible = useMemo(
    () =>
      (catalog ?? []).filter((m) =>
        filter ? m.name.toLowerCase().includes(filter.toLowerCase()) : true,
      ),
    [catalog, filter],
  );
  const staleCount = (catalog ?? []).filter((m) => m.stale).length;
  const retiredCount = (catalog ?? []).filter((m) => !m.registered).length;
  const neverCount = (catalog ?? []).filter(
    (m) => m.never_ran && !m.unimplemented,
  ).length;
  const declinedCount = (catalog ?? []).filter((m) => m.unimplemented).length;
  const selectedEntry = (catalog ?? []).find((m) => m.name === selected) ?? null;

  if (error) return <p className="pp__error">{error}</p>;

  return (
    <div className="pp pp--split">
      <aside className="pp__list" aria-label="Metrics">
        <div className="pp__bar">
          <input
            className="pp__input"
            placeholder="filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button type="button" className="pp__btn" onClick={load}>
            Refresh
          </button>
        </div>
        {catalog === null ? (
          <p className="pp__dim">Loading the catalogue…</p>
        ) : (
          <>
            <p className="pp__dim">
              {catalog.length} metrics
              {retiredCount > 0 && (
                <>
                  {' · '}
                  <span className="pp__err">
                    {retiredCount} retired — run history, no definition
                  </span>
                </>
              )}
              {neverCount > 0 && ` · ${neverCount} never ran`}
              {declinedCount > 0 && ` · ${declinedCount} not implemented`}
              {staleCount > 0 && ` · ${staleCount} missed the latest pass`}
              {latestPass !== null && ` · pass ${relative(latestPass)}`}
            </p>
            <ul className="pp__items">
              {visible.map((m) => (
                <li key={`${m.name}-${m.version}`}>
                  <button
                    type="button"
                    className={`pp__item${selected === m.name ? ' is-active' : ''}${
                      m.stale ? ' is-stale' : ''
                    }`}
                    onClick={() => {
                      setSelected(m.name);
                      setScope('');
                    }}
                  >
                    <span className="pp__item-name">{m.name}</span>
                    <span className="pp__item-meta pp__dim">
                      {m.scope && <span className="pp__tag">{m.scope}</span>}
                      {!m.registered ? (
                        <span
                          className="pp__badge pp__badge--bad"
                          title={`Run history with no registered definition — retired. Last ran ${relative(m.last_run_at_ms)}.`}
                        >
                          retired
                        </span>
                      ) : m.unimplemented ? (
                        <span
                          className="pp__badge"
                          title={m.how_to_enable ?? 'Declined on purpose.'}
                        >
                          not implemented
                        </span>
                      ) : m.never_ran ? (
                        <span
                          className="pp__badge pp__badge--warn"
                          title="Registered and never computed once."
                        >
                          never ran
                        </span>
                      ) : m.stale ? (
                        <span
                          className="pp__badge pp__badge--warn"
                          title={`Last ran ${relative(m.last_run_at_ms)}, before the latest pass.`}
                        >
                          stale
                        </span>
                      ) : (
                        <span
                          title={`${m.run_count} runs, mean ${dur(m.mean_duration_ms)}`}
                        >
                          {compact(m.rows_written)} rows ·{' '}
                          {dur(m.mean_duration_ms)}
                          {m.unit ? ` · ${m.unit}` : ''}
                        </span>
                      )}
                      {m.failed_runs > 0 && (
                        <span className="pp__err"> {m.failed_runs} failed</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>

      <section className="pp__detail-pane">
        {!selected ? (
          <p className="pp__dim">Pick a metric.</p>
        ) : (
          <>
            <div className="pp__bar">
              <span className="pp__strong">{selected}</span>
              {selectedEntry?.scope && (
                <span className="pp__tag">{selectedEntry.scope}</span>
              )}
              {selectedEntry?.unit && (
                <span className="pp__tag">{selectedEntry.unit}</span>
              )}
              <div className="pp__chips" role="group" aria-label="Window">
                {WINDOWS.map((w) => (
                  <button
                    key={w}
                    type="button"
                    className={`pp__chip${days === w ? ' is-active' : ''}`}
                    onClick={() => setDays(w)}
                  >
                    {w}d
                  </button>
                ))}
              </div>
              {series && series.scopes.length > 0 && (
                <label className="pp__filter">
                  <span>scope</span>
                  <select value={scope} onChange={(e) => setScope(e.target.value)}>
                    <option value="">all (mean)</option>
                    {series.scopes.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            {selectedEntry && (
              <div className="pp__about">
                {selectedEntry.description && (
                  <p className="pp__dim">{selectedEntry.description}</p>
                )}
                {selectedEntry.why_it_matters && (
                  <p className="pp__dim">
                    <strong>Why it matters.</strong>{' '}
                    {selectedEntry.why_it_matters}
                  </p>
                )}
                {selectedEntry.data_sources.length > 0 && (
                  <p className="pp__dim">
                    Reads:{' '}
                    {selectedEntry.data_sources.map((d) => (
                      <code key={d}>{d} </code>
                    ))}
                  </p>
                )}
                {selectedEntry.unimplemented && (
                  <p className="pp__err">
                    Not implemented.{' '}
                    {selectedEntry.how_to_enable ??
                      'No enabling instructions recorded.'}
                  </p>
                )}
                {!selectedEntry.registered && (
                  <p className="pp__err">
                    No registered definition — this metric was retired and its
                    rows remain. Nothing recomputes them.
                  </p>
                )}
              </div>
            )}

            {seriesError ? (
              <p className="pp__error">{seriesError}</p>
            ) : series === null ? (
              <p className="pp__dim">Loading…</p>
            ) : (
              <>
                {series.aggregate && (
                  <p className="pp__dim">
                    A <strong>mean across every scope</strong>, not one
                    subject — the shape is meaningful, the number is not.
                    Pick a scope for a real series.
                  </p>
                )}
                <Chart series={series} />
                <p className="pp__dim">
                  {series.points.length} points over {series.window_days} days
                  {series.truncated && ' · truncated at the route cap'}
                  {series.scopes_truncated &&
                    ` · ${series.scopes.length}+ scopes, list capped`}
                </p>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
